import { cn } from "@oneleet/utils";

import { VariantProps, cva } from "class-variance-authority";

const DEFAULT_COLOR = "green";

const stepProgressIndicatorVariants = cva(cn("flex w-full gap-1.5"), {
  variants: {
    color: {
      brand: "bg-brand-500 border-brand-600",
      accent: "bg-accent-800 border-accent-900",
      red: "bg-ng-red-800 border-ng-red-900",
      green: "bg-ng-green-800 border-ng-green-900",
      blue: "bg-ng-blue-800 border-ng-blue-900",
      yellow: "bg-ng-yellow-800 border-ng-yellow-900",
    },
  },
  defaultVariants: {
    color: DEFAULT_COLOR,
  },
});

type StepProgressIndicatorProps = VariantProps<
  typeof stepProgressIndicatorVariants
> & {
  totalSteps?: number;
  completedSteps?: number;
  className?: string;
};

/**
 * The StepProgressIndicator component is used to display a progress indicator as a series of filled in blocks horizontally.
 *
 * You can pass a numeric value to the `totalSteps` prop to indicate the number of steps of a task. The `completedSteps` prop indicates the progress completed. If the `completedSteps` value is greater than `totalSteps`, a warning message will be logged to the console.
 *
 * @example
 * ```jsx
 * <StepProgressIndicator totalSteps={10} completedSteps={5} />
 * ```
 */
export const StepProgressIndicator = ({
  color = "green",
  totalSteps = 3,
  completedSteps = 0,
  className,
}: StepProgressIndicatorProps) => {
  if (completedSteps > totalSteps) {
    console.warn(
      "StepProgressIndicator: completedSteps is greater than totalSteps",
    );
  }

  const width = 100 / totalSteps;
  return (
    <div className={cn("flex justify-evenly gap-1.5", className)}>
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={index}
          style={{ width: `${width}%` }}
          className={cn(
            completedSteps >= index + 1
              ? [stepProgressIndicatorVariants({ color })]
              : "border-neutral-300",

            "h-3 min-w-6 flex-1 rounded-[4px] border",
          )}
        />
      ))}
    </div>
  );
};
